"use client";

import React from "react";
import Link from "next/link";
import {
  Clock3,
  Headphones,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { FaInstagram, FaWhatsapp, FaYoutube } from "react-icons/fa";

import { SiteEdgeDivider } from "@/components/ui/site-edge-divider";
import Layout from "@/lib/Layout";
import FormContact from "./FromContact";

export type PhoneLine = {
  number: string;
  supportExt: string;
  inPersonExt: string;
  remoteExt: string;
  salesExt: string;
};

export type ContactViewData = {
  title: string;
  subtitle: string;
  sectionTitles?: {
    info?: string;
    socials?: string;
    form?: string;
    map?: string;
  };
  quickActions: {
    primaryPhone: { label: string; display: string; tel?: string };
    whatsapp: { label: string; display: string; href: string };
    email: { label: string; display: string; href: string };
    map: { label: string; display: string; href: string };
  };
  infoPanel: {
    phone: { label: string; display: string };
    email: { label: string; display: string };
    hours: { label: string; display: string };
    address: { label: string; display: string; mapHref: string };
  };
  whatsappSupport: {
    title: string;
    description: string;
    ctaLabel: string;
    href: string;
  };
  phoneLines: PhoneLine[];
  socials: Array<{ label: string; value: string; href: string }>;
  form: { title: string; description: string };
  mapSection: {
    title: string;
    hint: string;
    openLabel: string;
    href: string;
    embedSrc: string;
  };
  mobileBar: { label: string };
};

const ContactView = ({ data }: { data: ContactViewData }) => {
  const primaryPhone = data.quickActions.primaryPhone.display;
  const primaryTel =
    data.quickActions.primaryPhone.tel || `tel:${primaryPhone.replace(/-/g, "")}`;
  const whatsappHref =
    data.whatsappSupport.href || data.quickActions.whatsapp.href;
  const mailHref = data.quickActions.email.href;
  const phoneParts = data.infoPanel.phone.display.split("(");
  const phoneNumber = phoneParts[0]?.trim() || primaryPhone;
  const phoneExtension = phoneParts[1]
    ? `(${phoneParts.slice(1).join("(")}`
    : data.phoneLines[0]?.supportExt
      ? `(داخلی ${data.phoneLines[0].supportExt})`
      : "";
  const instagram = data.socials.find((social) =>
    social.label.includes("اینستاگرام"),
  );
  const videoSocial =
    data.socials.find((social) => social !== instagram) || data.socials[0];

  return (
    <Layout showDock={false}>
      <div
        className="min-h-screen bg-[#f8f8f8] text-[#20242c] dark:bg-slate-950 dark:text-slate-100"
        dir="rtl"
      >
        <SiteEdgeDivider />

        <main className="mx-auto w-full max-w-[1340px] px-4 py-8 sm:px-6 sm:py-10 lg:px-0 lg:py-12">
          <section
            aria-labelledby="contact-title"
            className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,59fr)_minmax(0,41fr)] lg:gap-6"
          >
            <section className="order-1 pt-1 lg:order-1">
              <header>
                <h1
                  id="contact-title"
                  className="text-[24px] font-black leading-[1.45] tracking-[-0.02em] sm:text-[28px] lg:text-[32px]"
                >
                  {data.title}
                </h1>
                <p className="mt-1.5 text-[12px] leading-6 text-[#626873] sm:text-[13px] lg:text-sm dark:text-slate-400">
                  {data.subtitle}
                </p>
              </header>

              <div className="mt-3 flex min-h-10 items-center justify-between rounded-lg border border-[#9fc2ff] bg-[#eef5ff] px-3 text-[11px] text-[#34435b] sm:text-xs">
                <span className="leading-5">
                  پاسخگویی تلفنی، {data.infoPanel.hours.display.replace("|", "،")}
                </span>
                <Clock3 className="h-[18px] w-[18px] shrink-0 text-[#155bd5]" />
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <ContactMethodCard
                  icon={<Phone className="h-5 w-5" />}
                  title="شماره تماس:"
                  value={phoneNumber}
                  extra={phoneExtension}
                  href={primaryTel}
                  actionLabel="تماس"
                  actionIcon={<Phone className="h-4 w-4" />}
                />
                <ContactMethodCard
                  icon={<Headphones className="h-5 w-5" />}
                  title="پشتیبانی:"
                  value={data.quickActions.whatsapp.display}
                  href={whatsappHref}
                  external
                  actionLabel="تماس"
                  actionIcon={<Phone className="h-4 w-4" />}
                />
                <ContactMethodCard
                  icon={<Mail className="h-5 w-5" />}
                  title="ایمیل:"
                  value={data.infoPanel.email.display}
                  href={mailHref}
                  actionLabel="ارسال"
                  actionIcon={<Mail className="h-4 w-4" />}
                  ltr
                />
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <SocialCard
                  icon={<FaWhatsapp className="h-4 w-4" />}
                  title="مشاوره خرید"
                  value={data.quickActions.whatsapp.display}
                  href={data.quickActions.whatsapp.href}
                />
                <SocialCard
                  icon={<FaWhatsapp className="h-4 w-4" />}
                  title="خدمات و پشتیبانی"
                  value={data.quickActions.whatsapp.display}
                  href={whatsappHref}
                />
                <SocialCard
                  icon={<FaYoutube className="h-4 w-4" />}
                  title="یوتیوب آسان جی‌اس‌ام"
                  value={videoSocial?.value || "ASANGSM"}
                  href={videoSocial?.href || "#"}
                />
                <SocialCard
                  icon={<FaInstagram className="h-4 w-4" />}
                  title="اینستاگرام آسان جی‌اس‌ام"
                  value={instagram?.value || "ASANGSM"}
                  href={instagram?.href || "#"}
                />
              </div>

              <section className="mt-3 flex h-[304px] flex-col rounded-xl bg-white p-4 shadow-[0_3px_14px_rgba(15,23,42,0.07)] dark:bg-slate-900">
                <div className="flex min-h-11 items-start gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#eaf2ff] text-[#155bd5] shadow-[0_3px_8px_rgba(21,91,213,0.18)]">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <h2 className="text-xs font-bold text-[#414752] dark:text-slate-200">
                      {data.infoPanel.address.label}:
                    </h2>
                    <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-[#3f4652] dark:text-slate-400">
                      {data.infoPanel.address.display}
                    </p>
                  </div>
                </div>

                <div className="relative mt-4 min-h-0 flex-1 overflow-hidden rounded-lg bg-[#edf1f5]">
                  <iframe
                    title="موقعیت آسان جی‌اس‌ام روی نقشه"
                    src={data.mapSection.embedSrc}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="h-full w-full border-0"
                    allowFullScreen
                  />
                  <SmartLink
                    href={data.mapSection.href}
                    external
                    className="absolute bottom-2 right-2 inline-flex h-8 items-center gap-1.5 rounded-md bg-white px-2.5 text-[10px] font-bold text-[#252a32] shadow-md transition hover:bg-[#f7f9fc]"
                  >
                    <MapPin className="h-4 w-4 text-[#155bd5]" />
                    مشاهده در نقشه
                  </SmartLink>
                </div>
              </section>
            </section>

            <section className="order-2 rounded-xl bg-white p-5 shadow-[0_3px_16px_rgba(15,23,42,0.08)] sm:p-6 lg:order-2">
              <h2 className="text-[22px] font-black leading-[1.8] sm:text-[26px]">
                {data.form.title}
              </h2>
              <p className="mt-0.5 text-[11px] leading-6 text-[#626873] sm:text-xs dark:text-slate-400">
                {data.form.description}
              </p>
              <FormContact />
            </section>
          </section>
        </main>
      </div>
    </Layout>
  );
};

export default ContactView;

function ContactMethodCard({
  icon,
  title,
  value,
  extra,
  href,
  actionLabel,
  actionIcon,
  external,
  ltr,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  extra?: string;
  href: string;
  actionLabel: string;
  actionIcon: React.ReactNode;
  external?: boolean;
  ltr?: boolean;
}) {
  return (
    <article className="flex h-[158px] flex-col rounded-xl bg-white p-4 shadow-[0_3px_14px_rgba(15,23,42,0.07)] dark:bg-slate-900">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#eaf2ff] text-[#155bd5] shadow-[0_3px_8px_rgba(21,91,213,0.18)]">
        {icon}
      </span>

      <div className="mt-auto flex items-end justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[11px] font-medium text-[#333945] dark:text-slate-300">
            {title}
          </h3>
          <p
            dir={ltr ? "ltr" : undefined}
            className={`mt-1 truncate text-[12px] font-extrabold text-[#252a32] dark:text-slate-100 ${
              ltr ? "text-right" : ""
            }`}
          >
            {value}
          </p>
          {extra ? (
            <p className="mt-0.5 text-[10px] text-[#4e5561] dark:text-slate-400">
              {extra}
            </p>
          ) : null}
        </div>

        <SmartLink
          href={href}
          external={external}
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-[#1763e7] px-2.5 text-[10px] font-bold text-[#155bd5] transition hover:bg-[#eef5ff]"
        >
          {actionIcon}
          {actionLabel}
        </SmartLink>
      </div>
    </article>
  );
}

function SocialCard({
  icon,
  title,
  value,
  href,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  href: string;
}) {
  return (
    <SmartLink
      href={href}
      external
      className="flex h-16 min-w-0 items-center gap-2 rounded-lg bg-white px-3 shadow-[0_3px_12px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-900"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eaf2ff] text-[#155bd5]">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[9px] text-[#444b57] dark:text-slate-400">
          {title}
        </span>
        <span dir="ltr" className="mt-1 block truncate text-right text-[10px] font-extrabold uppercase text-[#343a45] dark:text-slate-200">
          {value}
        </span>
      </span>
    </SmartLink>
  );
}

function SmartLink({
  href,
  external,
  className,
  children,
}: {
  href: string;
  external?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const isProtocolLink =
    href.startsWith("tel:") || href.startsWith("mailto:");
  const isExternal = external || href.startsWith("http");

  if (isProtocolLink) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}
